import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Autocomplete, AutocompleteOption, TeamAutocomplete } from "src/components";
import { MetricDto, useGetMetricCollection } from "src/http";
import { ITeamSummary } from "src/models";

interface ExistingMetricFormState {
  team: ITeamSummary | null;
  metric: MetricDto | null;
}

interface Props {
  visible: boolean;
  onAdded: (metricGuid: string) => void;
  onCancelled: () => void;
  excludedTeamSlug?: string;
}

const AddExistingMetricModal = (props: Props) => {
  const { formState, control, handleSubmit, reset, setValue } = useForm<ExistingMetricFormState>({
    defaultValues: {
      team: null,
      metric: null,
    },
  });
  const team = useWatch({ control, name: "team" });
  const { data: metrics, isInitialLoading } = useGetMetricCollection(team?.metricCollectionGuid ?? null);

  useEffect(() => {
    reset({}, { keepDefaultValues: true });
  }, [props.visible, reset]);

  function handleAddClicked() {
    handleSubmit((formState) => {
      props.onAdded(formState.metric!.guid);
    })();
  }

  function handleClose(_: object, reason: string) {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }

    handleCancelClicked();
  }

  function handleCancelClicked() {
    props.onCancelled();
  }

  return (
    <Dialog open={props.visible} onClose={handleClose}>
      <DialogTitle>Add Existing Metric</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <Stack spacing={0.25}>
            <Typography variant="label">Team *</Typography>
            <Controller
              name="team"
              control={control}
              rules={{ required: { message: "Team is required", value: true } }}
              render={({ field }) => (
                <TeamAutocomplete
                  placeholder="Select team owning the metric..."
                  value={field.value}
                  optionsFilter={(team) => team.slug !== props.excludedTeamSlug}
                  groupByTeamMembership
                  onChange={(team) => {
                    field.onChange(team);
                    setValue("metric", null);
                  }}
                  error={formState.errors["team"] !== undefined}
                  helperText={formState.errors["team"]?.message}
                />
              )}
            />
          </Stack>
          <Stack spacing={0.25}>
            <Typography variant="label">Metric *</Typography>
            <Controller
              name="metric"
              control={control}
              rules={{ required: { message: "Metric is required", value: true } }}
              render={({ field }) => (
                <Autocomplete
                  isLoading={isInitialLoading}
                  disabled={team == null || isInitialLoading}
                  options={metrics?.filter((metric) => metric.assignedTeam.slug === team!.slug) ?? []}
                  getOptionLabel={(option) => option.title}
                  onChange={(_, value) => field.onChange(value)}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props} key={option.guid}>
                      {option.title}
                    </AutocompleteOption>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select metric..."
                      error={formState.errors["metric"] !== undefined}
                      helperText={formState.errors["metric"]?.message}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.guid === value.guid}
                  value={field.value}
                />
              )}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCancelClicked}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!formState.isValid} onClick={handleAddClicked}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { AddExistingMetricModal };
